import { useState, useEffect, useCallback } from "react";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "../../components/misc/Card";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../components/misc/Table";
import { Switch } from "../../components/misc/Switch";
import ReconfirmOverlay from "../../components/misc/ReconfirmOverlay";
import axios from "axios";
import { getAuthHeader } from "../../utils/authHeader";
import { toast } from "react-toastify";
import CustomToast from "../../components/misc/CustomToast";
import LoadingScreen from "../../screens/LoadingScreen";

interface FreeTrial {
    id: string;
    code: string;
    applicablePlan: string;
    trialDays: number;
    maxUserCount: number;
    currentUsageCount: number;
    expiresAt: string;
    isActive: boolean;
    createdAt: string;
}

export const FreeTrialList = () => {
    const [codes, setCodes] = useState<FreeTrial[]>([]);
    const [loading, setLoading] = useState(true);
    const [isReconfirmOpen, setReconfirmOpen] = useState(false);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [pendingActiveState, setPendingActiveState] =
        useState<boolean>(false);

    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const fetchCodes = useCallback(async () => {
        setLoading(true);
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const response = await axios.get<FreeTrial[]>(
                `${backendUrl}/admin/free-trials`,
                { headers }
            );
            setCodes(response.data);
        } catch (error) {
            console.error(error);
            toast.error(
                <CustomToast
                    message="Failed to load free trial codes"
                    type="error"
                />
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCodes();
    }, [fetchCodes]);

    const toggleActive = async (id: string, active: boolean) => {
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            await axios.patch(
                `${backendUrl}/admin/free-trials/${id}`,
                { isActive: active },
                { headers }
            );
            toast.success(
                <CustomToast message="Status updated" type="success" />
            );
            fetchCodes();
        } catch (error) {
            console.error(error);
            toast.error(
                <CustomToast message="Error updating status" type="error" />
            );
        }
    };

    const handleToggleClick = (id: string, newState: boolean) => {
        console.log(`Toggling code ${id} to ${newState}`);
        setSelectedId(id);
        setPendingActiveState(newState);
        setReconfirmOpen(true);
    };

    const confirmToggle = async () => {
        if (!selectedId) return;
        await toggleActive(selectedId, pendingActiveState);
        setReconfirmOpen(false);
        setSelectedId(null);
    };

    if (loading) return <LoadingScreen />;

    return (
        <>
            <Card>
                <CardHeader>
                    <CardTitle>Free Trial Codes</CardTitle>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Code</TableHead>
                                <TableHead>Plan</TableHead>
                                <TableHead>Duration</TableHead>
                                <TableHead>Usage</TableHead>
                                <TableHead>Expires</TableHead>
                                <TableHead>Active</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {codes.map((c) => (
                                <TableRow key={c.id}>
                                    <TableCell className="font-mono">
                                        {c.code}
                                    </TableCell>
                                    <TableCell>
                                        {c.applicablePlan} Plan
                                    </TableCell>
                                    <TableCell>{c.trialDays} days</TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-1">
                                            {c.currentUsageCount || 0}/
                                            {c.maxUserCount}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-1">
                                            {new Date(
                                                c.expiresAt
                                            ).toLocaleString(undefined, {
                                                timeZone: browserTimezone,
                                                weekday: "short",
                                                year: "numeric",
                                                month: "long",
                                                day: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                            })}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <Switch
                                            checked={c.isActive}
                                            onCheckedChange={(val) =>
                                                handleToggleClick(c.code, val)
                                            }
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {codes.length === 0 && (
                        <div className="py-8 text-center text-muted-foreground">
                            No free trial codes found.
                        </div>
                    )}
                </CardContent>
            </Card>

            <ReconfirmOverlay
                isOpen={isReconfirmOpen}
                onClose={() => {
                    setReconfirmOpen(false);
                    setSelectedId(null);
                }}
                onConfirm={confirmToggle}
                title="Confirm Status Change"
                message={
                    pendingActiveState
                        ? "Are you sure you want to activate this code?"
                        : "Are you sure you want to deactivate this code?"
                }
                messageTwo=""
                confirmText="Confirm"
            />
        </>
    );
};

export default FreeTrialList;
